import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'

export type BrowserStorageOptions<T> = {
  /** Storage backend to use. Defaults to localStorage. */
  storage?: Storage
  /**
   * Deserializes the raw stored string. Return null to fall back to `initial`.
   * Defaults to a safe JSON.parse wrapper — suitable for primitives and plain
   * objects. Pass a custom function when shape validation is required.
   */
  parse?: (raw: string) => T | null
}

function defaultParse<T>(raw: string): T | null {
  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

/**
 * Backs a piece of React state with a browser storage backend (localStorage by
 * default, or sessionStorage when passed via options).
 *
 * Initializes from the stored value (via `parse`), falls back to `initial` when
 * the key is absent or `parse` returns null. Removes the key when state equals
 * `initial` (compared by JSON serialization); otherwise writes JSON.stringify(state).
 *
 * @param key - storage key
 * @param initial - fallback / "empty" value; also signals when to remove the key
 * @param options - optional storage backend and parse function
 */
function useBrowserStorage<T>(
  key: string,
  initial: T,
  options?: BrowserStorageOptions<T>
): [T, Dispatch<SetStateAction<T>>] {
  const { storage = localStorage, parse = defaultParse } = options ?? {}

  // Capture initial once on mount — same semantics as useState's initial value.
  // Keeping it out of the useEffect deps prevents storage I/O on every render
  // when the caller passes an unstable reference (e.g. a literal []).
  const initialRef = useRef(initial)

  const [value, setValue] = useState<T>(() => {
    try {
      const raw = storage.getItem(key)
      if (raw === null) return initialRef.current
      return parse(raw) ?? initialRef.current
    } catch {
      return initialRef.current
    }
  })

  useEffect(() => {
    if (JSON.stringify(value) === JSON.stringify(initialRef.current)) {
      storage.removeItem(key)
    } else {
      storage.setItem(key, JSON.stringify(value))
    }
  }, [key, value, storage])

  return [value, setValue]
}

export default useBrowserStorage
